/**
 * Export Render Path - Render one shader frame for video export
 *
 * Renders one frame at a given time with precomputed audio state to an offscreen
 * canvas at export resolution. Uses the same graph and compiler as live preview;
 * sets time and audio uniforms from the offline audio provider's frame state.
 * No live AudioManager or real-time playback.
 */

import type { NodeGraph } from '../data-model/types';
import type { AudioSetup } from '../data-model/audioSetupTypes';
import type { CompilationResult } from '../compile-contract';
import type { ShaderCompiler } from '../runtime/types';
import { ShaderInstance } from '../runtime/ShaderInstance';
import type { FrameAudioState } from './OfflineAudioProvider';
import { isRuntimeOnlyParameter } from '../utils/runtimeOnlyParams';
import { isParameterUniformSuppressedByConnection } from '../utils/resolveParameterInputMode';
import { refreshExportArrangementBakeCaches } from './refreshExportArrangementBakeCaches';
import {
  waitForWebGlExportCommands,
  waitForWebGlExportCommandsAsync,
} from './waitForWebGlExportCommands';

export interface ExportRenderPathConfig {
  /** Export width in pixels */
  width: number;
  /** Export height in pixels */
  height: number;
  /** Frame rate (e.g. 30); time = frameIndex / frameRate */
  frameRate: number;
  /** Start time offset (seconds) applied to uTime; video timestamps still start at 0. */
  startTimeSeconds?: number;
}

export interface ExportRenderPathResult {
  /**
   * Render one frame at frameIndex with precomputed audio state.
   * Sets uTime, uTimelineTime (from frameState.timelineTime), applies frameState.uniformUpdates, draws, returns canvas.
   */
  renderFrame(frameIndex: number, frameState: FrameAudioState): HTMLCanvasElement | OffscreenCanvas;
  /**
   * Optional async rendering hook for backends that require explicit GPU synchronization.
   * When present, callers should prefer this over `renderFrame`.
   */
  renderFrameAsync?: (
    frameIndex: number,
    frameState: FrameAudioState
  ) => Promise<HTMLCanvasElement | OffscreenCanvas>;
  /** Canvas at export resolution (width x height). Same reference every frame. */
  getCanvas(): HTMLCanvasElement | OffscreenCanvas;
  /** Release WebGL and shader resources. Call when export is done or cancelled. */
  dispose(): void;
}

/**
 * Create an export render path: offscreen canvas, compile graph once, render frames with time + audio uniforms.
 *
 * @param graph - Current node graph (same as live preview)
 * @param compiler - NodeShaderCompiler or any ShaderCompiler
 * @param audioSetup - Panel audio setup for audio-derived uniforms (bands/remappers/files).
 * @param config - width, height, frameRate
 * @returns Object with renderFrame(frameIndex, frameState), getCanvas(), dispose()
 */
export function createExportRenderPath(
  graph: NodeGraph,
  compiler: ShaderCompiler,
  audioSetup: AudioSetup,
  config: ExportRenderPathConfig
): ExportRenderPathResult {
  return createExportRenderPathImpl(graph, compiler, audioSetup, config);
}

/**
 * Alias for createExportRenderPath with positional args for orchestrator (03).
 * createExportRenderer(graph, compiler, audioSetup, width, height, frameRate) → { renderFrame, getCanvas, dispose }.
 */
export function createExportRenderer(
  graph: NodeGraph,
  compiler: ShaderCompiler,
  audioSetup: AudioSetup,
  width: number,
  height: number,
  frameRate: number
): ExportRenderPathResult {
  return createExportRenderPath(graph, compiler, audioSetup, { width, height, frameRate });
}

function createExportRenderPathImpl(
  graph: NodeGraph,
  compiler: ShaderCompiler,
  audioSetup: AudioSetup,
  config: ExportRenderPathConfig
): ExportRenderPathResult {
  const { width, height, frameRate } = config;
  const startTimeSeconds = config.startTimeSeconds ?? 0;

  // Prefer HTMLCanvasElement for export stability. In some browser paths,
  // OffscreenCanvas + WebGL2 + WebCodecs capture can exhibit intermittent black frames.
  const canvas = Object.assign(document.createElement('canvas'), { width, height }) as HTMLCanvasElement;

  if (!('width' in canvas && canvas.width === width && canvas.height === height)) {
    canvas.width = width;
    canvas.height = height;
  }

  const gl = canvas.getContext('webgl2', {
    antialias: false,
    preserveDrawingBuffer: true,
  });

  if (!gl) {
    throw new Error('WebGL2 not available for export');
  }
  const glContext: WebGL2RenderingContext = gl;

  const compilationResult: CompilationResult = compiler.compile(graph, audioSetup);
  if (compilationResult.metadata.errors.length > 0) {
    throw new Error(`Shader compilation failed: ${compilationResult.metadata.errors.join('; ')}`);
  }

  refreshExportArrangementBakeCaches(graph, audioSetup);

  const shaderInstance = new ShaderInstance(glContext, compilationResult);
  shaderInstance.setResolution(width, height);

  transferParametersFromGraph(graph, shaderInstance);

  let disposed = false;

  function drawExportFrame(frameIndex: number, frameState: FrameAudioState): void {
    if (disposed) {
      throw new Error('ExportRenderPath already disposed');
    }

    const time = startTimeSeconds + frameIndex / frameRate;
    shaderInstance.setTime(time);
    shaderInstance.setTimelineTime(frameState.timelineTime);
    shaderInstance.setParameters(frameState.uniformUpdates);

    glContext.viewport(0, 0, width, height);
    glContext.clearColor(0, 0, 0, 1);
    glContext.clear(glContext.COLOR_BUFFER_BIT);
    shaderInstance.render(width, height);
  }

  function renderFrame(frameIndex: number, frameState: FrameAudioState): HTMLCanvasElement | OffscreenCanvas {
    drawExportFrame(frameIndex, frameState);
    // Fence + clientWaitSync (finish fallback). See waitForWebGlExportCommands.ts.
    waitForWebGlExportCommands(glContext);
    return canvas;
  }

  async function renderFrameAsync(
    frameIndex: number,
    frameState: FrameAudioState
  ): Promise<HTMLCanvasElement | OffscreenCanvas> {
    drawExportFrame(frameIndex, frameState);
    // Async fence poll so video export can yield while waiting on the GPU.
    await waitForWebGlExportCommandsAsync(glContext);
    return canvas;
  }

  function dispose(): void {
    if (disposed) return;
    shaderInstance.destroy();
    try {
      const lose = glContext.getExtension('WEBGL_lose_context') as { loseContext?: () => void } | null;
      lose?.loseContext?.();
    } catch {
      // best-effort cleanup; ignore
    }
    try {
      // Encourage release of GPU resources eagerly in harness/export churn scenarios.
      canvas.width = 1;
      canvas.height = 1;
    } catch {
      // ignore
    }
    disposed = true;
  }

  return {
    renderFrame,
    renderFrameAsync,
    getCanvas: () => canvas,
    dispose,
  };
}

/**
 * Set shader parameters from graph (same logic as live: skip runtime-only and override-connected).
 */
function transferParametersFromGraph(graph: NodeGraph, shaderInstance: ShaderInstance): void {
  for (const node of graph.nodes) {
    for (const [paramName, value] of Object.entries(node.parameters)) {
      if (isRuntimeOnlyParameter(node.type, paramName)) {
        continue;
      }

      if (isParameterUniformSuppressedByConnection(graph, node, paramName)) {
        continue;
      }

      if (typeof value === 'number') {
        shaderInstance.setParameter(node.id, paramName, value);
      }
    }
  }
}
